/**
 * Briques visuelles communes à toutes les étapes de l'onboarding (tous rôles) :
 * barre de progression, retour, pied d'écran avec le bouton principal (fixé en
 * bas sur mobile, safe area iOS ; dans le flux sur grand écran).
 */

import type { ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@jeraisonne/ui';

export function OnboardingSteps({ labels, current }: { labels: string[]; current: number }) {
  return (
    <ol aria-label="Progression" className="mb-4 flex items-center gap-2">
      {labels.map((label, i) => (
        <li key={label} className="flex min-w-0 flex-1 flex-col gap-1">
          <span
            aria-hidden
            className={cn(
              'h-1.5 rounded-full transition-colors duration-500',
              i <= current ? 'bg-primary-500' : 'bg-ink-100 ocean:bg-[rgba(255,255,255,0.22)]',
            )}
          />
          <span
            className={cn(
              'truncate text-xs',
              i === current ? 'text-ink-900 font-semibold' : 'text-ink-500',
            )}
            aria-current={i === current ? 'step' : undefined}
          >
            {label}
          </span>
        </li>
      ))}
    </ol>
  );
}

export function OnboardingBack({
  onClick,
  label = 'Retour',
}: {
  onClick: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-ink-600 hover:text-ink-900 -ml-2 mb-2 inline-flex min-h-[2.75rem] items-center gap-1 rounded-xl px-2 text-sm font-semibold active:scale-[0.98]"
    >
      <ChevronLeft className="h-4 w-4" aria-hidden />
      {label}
    </button>
  );
}

/**
 * Pied d'étape : le bouton principal reste visible (pouce) quelle que soit la
 * longueur de l'étape. À placer dans un conteneur qui réserve la place
 * (`ONBOARDING_FOOTER_SPACE`).
 */
export function OnboardingFooter({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div
      data-testid="onboarding-footer"
      className="border-ink-100 ocean:border-white/10 ocean:bg-[#061433]/95 sm:ocean:bg-transparent fixed inset-x-0 bottom-0 z-20 border-t bg-white/95 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur sm:static sm:z-auto sm:mt-6 sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none"
    >
      <div className="mx-auto flex max-w-2xl flex-col gap-2">
        {hint && <p className="text-ink-500 text-center text-xs">{hint}</p>}
        {children}
      </div>
    </div>
  );
}

/** Marge basse des étapes, pour que le pied fixé ne masque pas la fin du contenu (mobile). */
export const ONBOARDING_FOOTER_SPACE = 'pb-36 sm:pb-0';

/** Bouton de choix (classe, formation, oui/non…) : 44 px, état pressé lisible en sombre. */
export function ChoiceChip({
  selected,
  onClick,
  children,
  className,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'min-h-[2.75rem] rounded-xl border-2 px-2 py-2 text-sm font-semibold transition-colors active:scale-[0.98]',
        selected
          ? 'border-primary-500 bg-primary-50 text-primary-700 ocean:bg-primary-500/15'
          : 'border-ink-100 text-ink-700 hover:border-primary-300 ocean:bg-white/5 bg-white',
        className,
      )}
    >
      {children}
    </button>
  );
}
